import React, { useRef, useEffect } from "react"
import { Card, CardBody, Row, Col, Spinner } from "reactstrap"
import { useSelector } from "react-redux"
import PrimaryButton from "newcomponents/primary-button"
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth"
import AddIcon from "@mui/icons-material/Add"
import RefreshIcon from "@mui/icons-material/Refresh"
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined"
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined"
import WavingHandOutlinedIcon from "@mui/icons-material/WavingHandOutlined"
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined"

const Miniwidgets = ({
  reports = [],
  loading,
  callBack,
  viewMode,
  setViewMode,
  fromDate,
  toDate,
  setFromDate,
  setToDate,
  noFilter,
  onSync,
  syncing,
}) => {
  // 🔁 Auto-trigger callback when both dates selected
  useEffect(() => {
    if (fromDate && toDate) {
      setViewMode("Custom")
      callBack()
    }
  }, [fromDate, toDate])

  const mode = useSelector(state => state.Layout.layoutMode)
  const fromDateRef = useRef(null)
  const toDateRef = useRef(null)

  const formatDateDisplay = dateStr => {
    if (!dateStr) return "dd/mm/yyyy"
    const date = new Date(dateStr)
    return `${String(date.getDate()).padStart(2, "0")}/${String(
      date.getMonth() + 1,
    ).padStart(2, "0")}/${date.getFullYear()}`
  }

  // 🔹 Dynamic column size (like MiniCards)
  const getColSize = count => {
    if (count === 1) return 12
    if (count === 2) return 6
    if (count === 3) return 4
    return 3
  }
  const colSize = getColSize(reports.length)

  // 🔹 Map icon keys to new MUI icons
  const iconMap = {
    group: <GroupOutlinedIcon style={{ fontSize: 26 }} />,
    like: <ThumbUpOutlinedIcon style={{ fontSize: 26 }} />,
    wave: <WavingHandOutlinedIcon style={{ fontSize: 26 }} />,
    star: <StarBorderOutlinedIcon style={{ fontSize: 26 }} />,
  }

  return (
    <React.Fragment>
      {/* 🔹 Header Bar */}
      <div
        style={{
          backgroundColor: mode === "dark" ? "#252f40" : "#fff",
          borderRadius: "16px",
          boxShadow:
            mode === "dark"
              ? "0px 2px 6px rgba(0,0,0,0.5)"
              : "0px 2px 6px rgba(0,0,0,0.1)",
          padding: "24px",
          marginBottom: "24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* Left Section */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <h1
            style={{
              margin: 0,
              fontSize: "28px",
              fontWeight: "600",
              color: mode === "dark" ? "#fff" : "#111827",
            }}
          >
            Facebook
          </h1>

          <PrimaryButton
            onClick={() => {
              window.location.href = `/dashboard/integrations`
            }}
            style={{
              padding: "6px 14px",
              fontSize: "13px",
              fontWeight: "500",
              borderRadius: "6px",
              border: "1.5px solid #2990DF",
              backgroundColor: "transparent",
              color: "#2990DF",
              textTransform: "uppercase",
            }}
          >
            Connected
          </PrimaryButton>
        </div>

        {/* Right Section (Dates + Create Button) */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          {/* Date Range Picker */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              backgroundColor: mode === "dark" ? "#1f2937" : "#F9FAFB",
              border:
                mode === "dark" ? "1px solid #374151" : "1px solid #E5E7EB",
              borderRadius: "8px",
              padding: "8px 12px",
              minWidth: "280px",
              gap: "8px",
            }}
          >
            {/* From Date */}
            <div
              style={{ position: "relative", flex: 1, cursor: "pointer" }}
              onClick={() => fromDateRef.current?.showPicker()}
            >
              <input
                ref={fromDateRef}
                type="date"
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
                style={{
                  position: "absolute",
                  inset: 0,
                  opacity: 0,
                  pointerEvents: "none",
                }}
              />
              <span
                style={{
                  color: fromDate
                    ? mode === "dark"
                      ? "#fff"
                      : "#111827"
                    : "#9CA3AF",
                  fontSize: "14px",
                }}
              >
                {fromDate ? formatDateDisplay(fromDate) : "dd/mm/yyyy"}
              </span>
            </div>

            <span style={{ color: "#9CA3AF" }}>-</span>

            {/* To Date */}
            <div
              style={{ position: "relative", flex: 1, cursor: "pointer" }}
              onClick={() => toDateRef.current?.showPicker()}
            >
              <input
                ref={toDateRef}
                type="date"
                value={toDate}
                onChange={e => setToDate(e.target.value)}
                style={{
                  position: "absolute",
                  inset: 0,
                  opacity: 0,
                  pointerEvents: "none",
                }}
              />
              <span
                style={{
                  color: toDate
                    ? mode === "dark"
                      ? "#fff"
                      : "#111827"
                    : "#9CA3AF",
                  fontSize: "14px",
                }}
              >
                {toDate ? formatDateDisplay(toDate) : "dd/mm/yyyy"}
              </span>
            </div>

            <CalendarMonthIcon
              size={18}
              style={{
                color: mode === "dark" ? "#9CA3AF" : "#6B7280",
                flexShrink: 0,
              }}
            />
          </div>

          {/* Sync Button */}
          <PrimaryButton
            onClick={onSync}
            disabled={syncing}
            style={{
              padding: "12px 20px",
              fontSize: "14px",
              fontWeight: "500",
              borderRadius: "8px",
              backgroundColor: "transparent",
              border: "1.5px solid #2990DF",
              color: "#2990DF",
            }}
          >
            {syncing ? (
              <Spinner size="sm" />
            ) : (
              <RefreshIcon style={{ fontSize: 18 }} />
            )}{" "}
            Sync
          </PrimaryButton>

          {/* Create New Button */}
          <PrimaryButton
            style={{
              padding: "12px 28px",
              fontSize: "16px",
              fontWeight: "500",
              borderRadius: "8px",
            }}
            icon={<AddIcon />}
            onClick={() =>
              (window.location.href = `/dashboard/facebook/create-post`)
            }
          >
            Create New Post
          </PrimaryButton>
        </div>
      </div>

      {/* 🔹 Cards */}
      <Row>
        {reports.map((report, key) => (
          <Col key={key} xl={colSize} lg={colSize} md={6} sm={12}>
            <Card
              style={{
                cursor: "default",
                border: "none",
                borderRadius: "16px",
                backgroundColor: mode === "dark" ? "#252f40" : "#fff",
                boxShadow:
                  mode === "dark"
                    ? "0px 2px 6px rgba(0,0,0,0.5)"
                    : "0px 2px 6px rgba(0,0,0,0.1)",
                transition: "all 0.3s ease",
                marginBottom: "20px",
              }}
            >
              <CardBody
                className="d-flex align-items-center justify-content-between"
                style={{ padding: "40px 24px" }}
              >
                {/* Left Side */}
                <div>
                  <h6
                    style={{
                      color: mode === "dark" ? "#9CA3AF" : "#6B7280",
                      marginBottom: "4px",
                      fontSize: "14px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {report.title}
                  </h6>
                  <h4
                    style={{
                      color: mode === "dark" ? "#fff" : "#111",
                      margin: 0,
                      fontWeight: 600,
                      fontSize: "22px",
                    }}
                  >
                    {loading ? (
                      <Spinner
                        size="sm"
                        color={mode === "dark" ? "light" : "dark"}
                      />
                    ) : (
                      report.total
                    )}
                  </h4>
                </div>

                {/* Right Side (MUI Icon instead of MDI) */}
                <div
                  style={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: mode === "dark" ? "#363F4F" : "#F1F1F1",
                    color:
                      report.badgecolor === "danger" ? "#2990DF" : "#2990DF",
                  }}
                >
                  {iconMap[report.iconClass] || (
                    <GroupOutlinedIcon style={{ fontSize: 26 }} />
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>
    </React.Fragment>
  )
}

export default Miniwidgets
